import { AxiosInstance } from "axios";
import { get } from "lodash-es";

import { getError } from "@/utils/get-error";
import { ResponseMESF, axiosInstance } from "@dexteel/mesf-core";

//dotenv.config()();

type getAllHeatsParams = {
	start?: string | null;
	end?: string | null;
	heatName?: string;
	interval?: "" | "latest" | "12h" | "24h";
	ladleId?: number | null;
};

export class HeatsRepository {
	private getAxiosInstance(): AxiosInstance {
		return axiosInstance;
	}
	async getAllHeats({
		start,
		end,
		heatName: heatNameValue,
		interval,
		ladleId,
	}: getAllHeatsParams) {
		const heatName = heatNameValue?.trim()?.length ? heatNameValue : null;

		const resp = await this.getAxiosInstance().get("/heat/all-by-ladles", {
			params: {
				start: start,
				end: end,
				heatName,
				interval,
				ladleId,
			},
		});
		return {
			error: false,
			data: resp.data,
		};
	}

	async getCurrentHeats() {
		const resp = await this.getAxiosInstance().get("/heat/current");
		return {
			error: false,
			data: resp.data,
		};
	}

	async getHeatsByTGIIdentifier(identifier: number) {
		try {
			const resp = await this.getAxiosInstance().get(`heat/${identifier}`);

			return {
				ok: true,
				data: resp.data.result,
			};
		} catch (e: any) {
			const message = getError(e);
			return Promise.reject(message);
		}
	}

	async updateEafTap({ heatId, eafName, timeStamp, ladleId, comments }: any) {
		const resp = await this.getAxiosInstance().patch(
			"/heat/eaf-tap",
			{},
			{
				params: {
					heatId,
					eafName,
					timeStamp,
					ladleId,
					comments,
				},
			},
		);
		return {
			error: false,
			data: resp.data,
		};
	}

	async updateLadleOpen({
		timeStamp,
		comments,
		casterName,
		heatId,
		tundishId,
	}: {
		timeStamp: string;
		comments: string;
		casterName: string;
		heatId: number;
		tundishId: number;
	}) {
		try {
			return await this.getAxiosInstance().patch(
				"/heat/ladle-open",
				{},
				{
					params: {
						timeStamp,
						comments,
						heatId,
						tundishId,
						casterName,
					},
				},
			);
		} catch (e: any) {
			return Promise.reject(getError(e));
		}
	}

	async updateLadleClose({
		timeStamp,
		comments,
		casterName,
		heatId,
	}: {
		timeStamp: string;
		comments: string;
		casterName: string;
		heatId: number;
	}) {
		try {
			return await this.getAxiosInstance().patch(
				"/heat/ladle-close",
				{},
				{
					params: {
						timeStamp,
						comments,
						heatId,
						casterName,
					},
				},
			);
		} catch (e) {
			return Promise.reject(get(e, "response.data.detail", e));
		}
	}

	async updateStirPerformance({
		stirPerformanceId,
		heatId,
		comments,
	}: { stirPerformanceId: number; heatId: number; comments?: string }) {
		const resp = await this.getAxiosInstance().patch(
			"/heat/stir-performance",
			{},
			{
				params: {
					heatId,
					stirPerformanceId,
					comments,
				},
			},
		);
		return {
			error: false,
			data: resp.data,
		};
	}

	async sortOrder({
		heatId,
		nextHeatId,
	}: { nextHeatId: number; heatId: number }) {
		const resp = await this.getAxiosInstance().patch(
			"/heat/sort-order",
			{},
			{
				params: {
					heatId,
					nextHeatId,
				},
			},
		);
		return {
			error: false,
			data: resp.data,
		};
	}

	async createHeat({
		heatName,
		ladleId,
	}: { heatName: number | string; ladleId: number }) {
		const resp = await this.getAxiosInstance().post(
			"/heat/new",
			{},
			{
				params: {
					heatName,
				},
			},
		);
		if (resp.status === 200) {
			const assignmentResponse = await this.getAxiosInstance().patch(
				"/heat/ladle-assignment",
				{},
				{
					params: {
						heatId: resp.data.result,
						rootId: ladleId,
					},
				},
			);

			if (assignmentResponse.status === 200) {
				return {
					error: false,
					data: assignmentResponse.data,
				};
			} else {
				return {
					error: true,
					data: assignmentResponse.data,
				};
			}
		} else
			return {
				error: true,
				data: resp.data,
			};
	}

	async getFutureHeats() {
		const resp = await this.getAxiosInstance().get("/heat/future");
		if (resp.status === 200)
			return {
				error: false,
				data: resp.data,
			};
		else
			return {
				error: true,
				data: resp.data,
			};
	}

	async getHeatEvents(heatId: number) {
		try {
			const resp = await this.getAxiosInstance().get("/heat/events", {
				params: {
					heatId,
				},
			});
			return {
				error: false,
				data: resp.data,
			};
		} catch (e: any) {
			const message = getError(e);
			return {
				error: true,
				data: message,
			};
		}
	}

	async updateLadleOpenType(
		heatId: number,
		ladleOpenTypeId: number,
		comments: string,
	) {
		try {
			const resp = await this.getAxiosInstance().patch(
				"/heat/ladle-open-type",
				{},
				{
					params: {
						heatId,
						ladleOpenTypeId,
						comments,
					},
				},
			);
			return resp.data;
		} catch (e: any) {
			return Promise.reject(getError(e));
		}
	}
	async updateWorkers(heatId: number, heatWorkers: string) {
		try {
			const resp = await this.getAxiosInstance().patch(
				"/heat/workers",
				{},
				{
					params: {
						heatId,
						heatWorkers,
					},
				},
			);
			return resp.data;
		} catch (e: any) {
			return Promise.reject(getError(e));
		}
	}

	async updateServiceLocation(
		heatId: number,
		programmedServiceLocationId: number,
	) {
		try {
			const resp = await this.getAxiosInstance().patch(
				"/heat/programmed-service-location",
				{},
				{
					params: {
						heatId,
						programmedServiceLocationId,
					},
				},
			);
			return resp.data;
		} catch (e: any) {
			const message = getError(e);
			return Promise.reject(message);
		}
	}

	async updateCasterName(heatId: number, programmedCasterName: string) {
		try {
			const resp = await this.getAxiosInstance().patch(
				"/heat/programmed-caster",
				{},
				{
					params: {
						heatId,
						programmedCasterName,
					},
				},
			);
			return resp.data;
		} catch (e: any) {
			const message = getError(e);
			return Promise.reject(message);
		}
	}
	async updateArgonBubble(
		heatId: number,
		stirMinutes2: number,
		stirMinutes1: number,
		comments: string,
	) {
		try {
			const resp = await this.getAxiosInstance().patch(
				"/heat/stir-minutes",
				{},
				{
					params: {
						heatId,
						stirMinutes2,
						stirMinutes1,
						comments,
					},
				},
			);
			return resp.data;
		} catch (e: any) {
			const message = getError(e);
			return Promise.reject(message);
		}
	}

	async updateLanceMinutes(heatId: number, lanceMinutes: number) {
		try {
			const resp = await this.getAxiosInstance().patch(
				"/heat/lance-minutes",
				{},
				{
					params: {
						heatId,
						lanceMinutes,
					},
				},
			);
			return resp.data;
		} catch (e: any) {
			const message = getError(e);
			return Promise.reject(message);
		}
	}
	async updateTapTemperature(heatId: number, tapTemperature: number) {
		try {
			const resp = await this.getAxiosInstance().patch(
				"/heat/tap-temperature",
				{},
				{
					params: {
						heatId,
						tapTemperature,
					},
				},
			);
			return resp.data;
		} catch (e: any) {
			const message = getError(e);
			return Promise.reject(message);
		}
	}

	async changeHeatLocation(heatId: number, locationId: number) {
		try {
			const resp = await this.getAxiosInstance().patch(
				"/heat/location",
				{},
				{
					params: {
						heatId,
						locationId,
					},
				},
			);
			return resp.data;
		} catch (e: any) {
			const message = getError(e);
			return Promise.reject(message);
		}
	}
	async changeLadleForHeat(heatId: number, ladleId: number) {
		try {
			const resp = await this.getAxiosInstance().patch(
				"/heat/ladle-assignment",
				{},
				{
					params: {
						heatId: heatId,
						rootId: ladleId,
					},
				},
			);
			return resp.data;
		} catch (e: any) {
			const message = getError(e);
			return Promise.reject(message);
		}
	}
	async dumpHeat(heatId: number, comments: string) {
		try {
			const resp = await this.getAxiosInstance().patch(
				"/heat/dump",
				{},
				{
					params: {
						heatId,
						comments,
					},
				},
			);
			return resp.data;
		} catch (e: any) {
			const message = getError(e);
			return Promise.reject(message);
		}
	}
	async setFinished(heatId: number, timeStamp: string | null) {
		try {
			const resp = await this.getAxiosInstance().patch(
				"/heat/finished-time",
				{},
				{
					params: {
						heatId,
						timeStamp,
					},
				},
			);
			return resp.data;
		} catch (e: any) {
			const message = getError(e);
			return Promise.reject(message);
		}
	}
	async updateHeatDatetimes(
		heatId: number,
		timestamp: Date | undefined | null,
		dtToChange: string,
	) {
		const timestampp = timestamp ? timestamp : "";

		try {
			const resp = await this.getAxiosInstance().patch(
				"/heat/datetimes",
				{},
				{
					params: {
						heatId,
						timestamp: timestampp,
						heatDateTime: dtToChange,
					},
				},
			);
			return {
				ok: true,
				data: resp.data,
			};
		} catch (e: any) {
			const message = getError(e);
			return {
				ok: false,
				data: message,
			};
		}
	}
	async getHeat(heatId: number) {
		try {
			const resp = await this.getAxiosInstance().get(`/heat/${heatId}`);
			return {
				ok: true,
				data: resp.data,
			};
		} catch (e: any) {
			const message = getError(e);
			return {
				ok: false,
				data: message,
			};
		}
	}
	async changeEafStart({
		heatId,
		timeStamp,
		eafName,
		comments = "",
	}: {
		heatId: number;
		timeStamp: string | Date | null;
		eafName: string;
		comments: string;
	}) {
		try {
			const resp = await this.getAxiosInstance().patch(
				`/heat/eaf-start`,
				{},
				{
					params: {
						heatId,
						eafName,
						timeStamp: timeStamp,
						comments,
					},
				},
			);
			return {
				ok: true,
				data: resp.data,
			};
		} catch (e: any) {
			const message = getError(e);
			return {
				ok: false,
				data: message,
			};
		}
	}
	async changeLocation({
		heatId,
		locationId,
	}: {
		heatId: number;
		locationId: number;
	}) {
		try {
			const resp = await this.getAxiosInstance().patch(
				`/heat/location`,
				{},
				{
					params: {
						heatId,
						locationId,
					},
				},
			);
			return {
				ok: true,
				data: resp.data,
			};
		} catch (e: any) {
			const message = getError(e);
			return {
				ok: false,
				data: message,
			};
		}
	}
	async setFirstInSequence({
		heatId,
		firstInSequence,
	}: {
		heatId: number;
		firstInSequence: boolean;
	}) {
		try {
			const resp = await this.getAxiosInstance().patch(
				`/heat/first-in-sequence`,
				{},
				{
					params: {
						heatId,
						firstInSequence,
					},
				},
			);
			return {
				ok: true,
				data: resp.data,
			};
		} catch (e: any) {
			const message = getError(e);
			return {
				ok: false,
				data: message,
			};
		}
	}

	async delete(heatId: number): Promise<ResponseMESF> {
		try {
			const resp = await this.getAxiosInstance().delete(`/heat/${heatId}`);
			return {
				ok: true,
				data: resp.data,
			};
		} catch (e: any) {
			const message = getError(e);
			return {
				ok: false,
				message: message,
			};
		}
	}
}

export const heatsRepository = new HeatsRepository();
